import { useCallback, RefObject } from "react";
import usePDA1081_R01Store from "./PDA1081_R01Store";
import { scanItemR02, returnMaterialsR02 } from "src/apis/materialReturnApi";
import { useToast } from "src/hooks/useToast";
import type {
  PDA1081_R01ReturnRequest,
  ReturnItem,
} from "./PDA1081_R01Dto";

export const usePDA1081_R01Content = (scanInputRef?: RefObject<HTMLInputElement>) => {
  const {
    scanValue,
    setScanValue,
    setScannedItem,
    returnQty,
    setReturnQty,
    setLoading,
    returnItems,
    addReturnItem,
    setReturnItems,
    removeReturnItem,
    clearReturnItems,
  } = usePDA1081_R01Store();

  const { showToast } = useToast();

  /** 품목 스캔 → 200 보유량 조회 후 반입량 자동 세팅 */
  const handleScanItem = useCallback(async (): Promise<boolean> => {
    const trimmed = scanValue.trim().toUpperCase();
    if (!trimmed) {
      showToast("error", "품목을 스캔하세요.");
      return false;
    }
    try {
      setLoading(true);
      const response = await scanItemR02(trimmed);
      const data = response?.data?.data;
      if (!data) {
        showToast("error", "스캔 결과를 받지 못했습니다.");
        setScanValue("");
        return false;
      }
      setScannedItem(data);
      setReturnQty(data.availableQty);
      showToast("success", `${data.itemNm} (반입가능 ${data.availableQty.toLocaleString()})`);
      return true;
    } catch (error: unknown) {
      console.error("품목 스캔 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "품목 스캔에 실패했습니다.";
      showToast("error", message);
      setScanValue("");
      setScannedItem(null);
      setReturnQty(0);
      return false;
    } finally {
      setLoading(false);
    }
  }, [scanValue, setLoading, setScanValue, setScannedItem, setReturnQty, showToast]);

  /** 검수: 반입 대기 목록에 추가 */
  const handleAddToGrid = useCallback(() => {
    const { scannedItem } = usePDA1081_R01Store.getState();
    if (!scannedItem) {
      showToast("error", "품목을 먼저 스캔하세요.");
      return;
    }
    if (!returnQty || returnQty <= 0) {
      showToast("error", "반입 수량은 0보다 커야 합니다.");
      return;
    }
    if (returnQty > scannedItem.availableQty) {
      showToast(
        "error",
        `반입가능 수량을 초과했습니다. (가능 ${scannedItem.availableQty.toLocaleString()})`
      );
      return;
    }

    const exists = returnItems.find((i) => i.itemCd === scannedItem.itemCd);
    if (exists) {
      const merged = exists.returnQty + returnQty;
      if (merged > scannedItem.availableQty) {
        showToast(
          "error",
          `누적 수량이 반입가능을 초과합니다. (가능 ${scannedItem.availableQty.toLocaleString()})`
        );
        return;
      }
      setReturnItems(
        returnItems.map((i) =>
          i.itemCd === scannedItem.itemCd ? { ...i, returnQty: merged } : i
        )
      );
      showToast("success", `${scannedItem.itemNm} 수량 합산 (${exists.returnQty} → ${merged})`);
    } else {
      const gridData: ReturnItem = {
        itemCd: scannedItem.itemCd,
        itemNm: scannedItem.itemNm,
        itemSpec: scannedItem.itemSpec,
        itemUnit: scannedItem.itemUnit,
        availableQty: scannedItem.availableQty,
        returnQty,
      };
      addReturnItem(gridData);
      showToast("success", `반입 대기 추가 (${returnItems.length + 1}건)`);
    }

    setScanValue("");
    setScannedItem(null);
    setReturnQty(0);
    setTimeout(() => scanInputRef?.current?.focus(), 50);
  }, [
    returnQty,
    returnItems,
    addReturnItem,
    setReturnItems,
    setScanValue,
    setScannedItem,
    setReturnQty,
    showToast,
    scanInputRef,
  ]);

  const handleRemoveFromGrid = useCallback(
    (itemCd: string) => {
      removeReturnItem(itemCd);
      showToast("info", "반입 대기 목록에서 삭제되었습니다.");
    },
    [removeReturnItem, showToast]
  );

  /** 전체 반입 등록 (백엔드 일괄) */
  const handleSaveAll = useCallback(async () => {
    if (returnItems.length === 0) {
      showToast("error", "반입할 항목이 없습니다.");
      return;
    }
    try {
      setLoading(true);
      const request: PDA1081_R01ReturnRequest = {
        warehouseCd: "",
        logicCenter: "",
        remark: "",
        itemList: returnItems.map((i) => ({ itemCd: i.itemCd, returnQty: i.returnQty })),
      };
      const response = await returnMaterialsR02(request);
      const result = response?.data?.data;

      if (result) {
        if (result.failCount === 0) {
          showToast(
            "success",
            `자재반입 완료 (${result.successCount}건, 총 ${result.totalReturnedQty.toLocaleString()})`
          );
          clearReturnItems();
        } else {
          const failed = result.results
            .filter((r) => !r.success)
            .map((r) => r.itemCd)
            .join(", ");
          showToast("warning", `${result.successCount}건 반입 / ${result.failCount}건 실패 (${failed})`);
          const failedCds = new Set(result.results.filter((r) => !r.success).map((r) => r.itemCd));
          setReturnItems(returnItems.filter((i) => failedCds.has(i.itemCd)));
        }
      }
    } catch (error: unknown) {
      console.error("자재반입 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "자재반입에 실패했습니다.";
      showToast("error", message);
    } finally {
      setLoading(false);
    }
  }, [returnItems, setLoading, clearReturnItems, setReturnItems, showToast]);

  const handleCancel = useCallback(() => {
    if (returnItems.length > 0) {
      if (!window.confirm(`반입 대기중인 ${returnItems.length}건이 있습니다.\n모두 취소하시겠습니까?`)) {
        return;
      }
      clearReturnItems();
      showToast("info", "반입 대기 목록이 초기화되었습니다.");
    }
    setScanValue("");
    setScannedItem(null);
    setReturnQty(0);
  }, [returnItems, clearReturnItems, setScanValue, setScannedItem, setReturnQty, showToast]);

  return {
    handleScanItem,
    handleAddToGrid,
    handleRemoveFromGrid,
    handleSaveAll,
    handleCancel,
  };
};
